import { ToastData, useToastContext } from './context';
import { ReactNode, useCallback } from 'react';

export interface Toast {
  close: () => void;
}
export type ShowToastFn = (
  content: ReactNode,
  options?: ShowToastOptions
) => Toast;

export interface ShowToastOptions {
  /**
   * Handler that is called when the toast is closed, either by the user
   * or after a timeout.
   */
  onClose?: () => void;
  /**
   * A timeout to automatically close the toast after, in milliseconds.
   * Note that if the toast is hovered or interacted with, the timeout is
   * paused.
   *
   * @default 3000
   */
  timeout?: number;
  variant?: 'neutral' | 'success' | 'error';
  /**
   * Optional title for the toast.
   */
  title?: ReactNode;
}

export const useShowToast = (): ShowToastFn => {
  const toastState = useToastContext();

  if (!toastState) {
    throw new Error('"useShowToast" should be used inside a <ToastProvider>.');
  }

  const { queue } = toastState;

  return useCallback((content, options) => {
    const {
      onClose,
      timeout = 3000,
      variant = 'neutral',
      title,
    } = options || {};

    const toastData: ToastData = {
      title,
      content,
      variant,
    };

    /*
     * NOTE:
     * There is an addition "priority" parameter to ".add".
     * We don't use it now, but we should if we will have different types of
     * toast notifications with different priorities.
     */
    const key = queue.add(toastData, { onClose, timeout });
    return {
      close: () => {
        queue.close(key);
      },
    };
  }, []);
};
